import type { RiskPoint } from "@/components/Dashboard/RiskStratificationCard/RiskStratificationChart";

// API Response types
export interface RiskChartApiResponse {
  status: number;
  data: {
    memberId: string;
    currentRiskScore: number;
    currentRiskCategory: string;
    period: string;
    dataRange: {
      oldestDate: string;
      newestDate: string;
      availableYears: number[];
      currentYear: number;
    };
    chartData: {
      year: number;
      quarters: {
        quarter: string;
        quarterLabel: string;
        score: number;
        category: string;
        scoreDate: string;
      }[];
    }[];
  };
}

export const transformChartData = (
  apiData: RiskChartApiResponse["data"]["chartData"]
): RiskPoint[] => {
  const result: RiskPoint[] = [];

  // Sort by year and quarter to ensure chronological order
  const sortedData = [...apiData].sort((a, b) => a.year - b.year);

  for (const yearData of sortedData) {
    for (const quarter of yearData.quarters) {
      result.push({
        date: quarter.quarterLabel,
        score: quarter.score,
      });
    }
  }

  return result;
};

export const filterRiskChartData = (
  allChartData: RiskPoint[],
  currentYear: number,
  selectedYears: string[] = []
): RiskPoint[] => {
  if (!allChartData.length) return [];

  // If years are selected, filter by those years
  if (selectedYears.length > 0) {
    return allChartData.filter((point) => {
      const year = extractYearFromQuarterLabel(point.date);
      return year && selectedYears.includes(year.toString());
    });
  }

  const currentYearData = allChartData.filter((point) => {
    const year = extractYearFromQuarterLabel(point.date);
    return year === currentYear;
  });

  const quartersNeeded = 5 - currentYearData.length;

  if (quartersNeeded > 0 && currentYearData.length < 5) {
    const previousYear = currentYear - 1;
    const previousYearData = allChartData
      .filter((point) => {
        const year = extractYearFromQuarterLabel(point.date);
        return year === previousYear;
      })
      .slice(-quartersNeeded); 
    return [...previousYearData, ...currentYearData];
  }
  return currentYearData.slice(-5);
};

const extractYearFromQuarterLabel = (quarterLabel: string): number | null => {
  const match = quarterLabel.match(/(19|20)\d{2}/);
  return match ? parseInt(match[0], 10) : null;
};

export const getAvailableYears = (
  chartData: RiskChartApiResponse["data"]["chartData"]
): number[] => {
  const years = chartData.map((item) => item.year);
  return [...new Set(years)].sort((a, b) => b - a); // Sort descending
};

